// Editorial content for /insights. Kept in one JS module so adding a post is
// a single object append — no CMS, no build-step magic. Body is JSX for max
// flexibility. Slugs are permanent and appear in sitemap.xml.
import React from "react";

export const INSIGHTS = [
  {
    slug: "ucs-vs-verra-redd",
    title: "UCS vs Verra REDD+ — why regulated beats unaudited",
    excerpt:
      "How the Brazilian UCS carbon credit — with ISIN, CPR Verde, and BMV Global methodology — solves the credibility crisis that erased 90% of certain Verra rainforest credits in 2023.",
    date: "2026-02-15",
    read: "6 min read",
    tags: ["UCS", "Verra", "REDD+", "Compliance"],
    body: (
      <>
        <p>
          In January 2023, an 18-month investigation by <em>The Guardian</em>, <em>Die Zeit</em>, and SourceMaterial
          concluded that <strong>more than 90% of rainforest credits</strong> certified by Verra — the world's
          largest voluntary carbon registry — were "phantom credits" that likely represented no real emissions
          reduction. The corporate voluntary carbon market has been in a credibility crisis ever since.
        </p>

        <h2>What went wrong at Verra</h2>
        <p>
          Verra's REDD+ methodologies allowed project developers to define their own baseline scenarios — the
          hypothetical rate of deforestation that would occur <em>without</em> the project. Independent audits
          repeatedly found these baselines inflated 3–5×, which meant every "avoided" ton of CO₂ sold to a
          corporate buyer was, on average, only worth 10–30% of a real ton. Buyers included Disney, Shell,
          Gucci, and easyJet — all of whom retired the credits publicly as "net-zero" claims.
        </p>

        <h2>How UCS is structurally different</h2>
        <ul>
          <li>
            <strong>Regulated instrument.</strong> UCS is not a "credit" — it is a{" "}
            <strong>Cédula de Produto Rural Verde</strong> (CPR Verde) issued under Brazilian Law 13.986/2020.
            Every unit is a financial asset backed by real forest under conservation, registered with the São
            Paulo stock exchange (B3) and assigned an <strong>ISIN code</strong>.
          </li>
          <li>
            <strong>Public verification.</strong> Anyone can look up any UCS unit balance directly at B3 using
            the issuer's CNPJ. This is a real ledger — not an opaque private registry.
          </li>
          <li>
            <strong>Tier-1 audit network.</strong> BMV Global projects are audited by <strong>SGS</strong>,{" "}
            <strong>TÜV Rheinland</strong>, <strong>Ernst & Young</strong>, and <strong>ERM</strong> — the same
            firms that audit Fortune 500 financial statements.
          </li>
          <li>
            <strong>Methodology under scrutiny.</strong> BMV's methodology is public, based on ISO 14064 and the
            IPCC Good Practice Guidance. It uses three pillars — economic, territorial, and environmental — and
            has been referenced by Brazilian regulators.
          </li>
          <li>
            <strong>No inflated baselines.</strong> UCS units are priced on{" "}
            <strong>hectares protected × timber stock × time</strong>, not on projected counterfactual emissions.
            You are buying a share of a specific forest, not a hypothesis about a forest that "would have been"
            cut down.
          </li>
        </ul>

        <h2>What this means for corporate buyers</h2>
        <p>
          Under the emerging <strong>SBTi 2030</strong> corporate net-zero framework and <strong>Article 6.4</strong>{" "}
          of the Paris Agreement, buyers will be legally required to disclose the <em>provenance</em> of every
          offset credit they retire. Unaudited REDD+ credits without a public registry cannot meet this bar.
          UCS — with its ISIN, B3 registration, and public verification — was designed exactly for this
          disclosure regime.
        </p>

        <p>
          If you are a sustainability officer facing 2030 disclosure deadlines,{" "}
          <a href="/marketplace">browse the GreenBelt marketplace</a> — every project shows its ISIN, audit
          firm, and B3 lookup link on the same page.
        </p>
      </>
    ),
  },

  {
    slug: "article-6-4-explained",
    title: "Article 6.4 explained — how the Paris Agreement is reshaping the voluntary carbon market",
    excerpt:
      "In 2025, Article 6.4 of the Paris Agreement created the first UN-supervised international carbon credit standard. Here's what it means for corporate ESG teams — and why regulated instruments like UCS are ahead of the curve.",
    date: "2026-02-08",
    read: "5 min read",
    tags: ["Article 6.4", "Paris Agreement", "SBTi", "Regulation"],
    body: (
      <>
        <p>
          At COP29 in Baku (November 2024), 196 parties formally activated <strong>Article 6.4</strong> of the
          Paris Agreement — the mechanism creating a single, UN-supervised carbon market to replace the
          patchwork of voluntary standards (Verra, Gold Standard, ACR) that dominated the last decade. As of
          2026, corporate buyers face a decisive choice: continue with legacy unregulated credits, or move to
          Article-6.4-aligned instruments.
        </p>

        <h2>The five pillars of Article 6.4</h2>
        <ul>
          <li>
            <strong>Supervisory Body oversight.</strong> A UN-appointed body (the "6.4 SB") certifies
            methodologies. No more registry-shopping between Verra and Gold Standard for the loosest baseline.
          </li>
          <li>
            <strong>Corresponding adjustment.</strong> Every credit exported must be deducted from the host
            country's inventory — preventing double-counting between the seller nation and the buyer nation.
          </li>
          <li>
            <strong>Share of Proceeds.</strong> 5% of every 6.4 credit funds an Adaptation Fund for climate-vulnerable
            countries. This is a compulsory levy, not optional.
          </li>
          <li>
            <strong>Mandatory removal component.</strong> Article 6.4 favours <em>removals</em> (real forest,
            direct-air capture) over pure <em>avoidance</em> (avoided deforestation). Long-cycle removals score
            higher than short-cycle avoidance.
          </li>
          <li>
            <strong>Public registry.</strong> Every 6.4 credit will be registered on a UN-run public ledger with
            unique serial numbers — very close to what B3 already provides for UCS.
          </li>
        </ul>

        <h2>Why UCS is well-positioned</h2>
        <p>
          The Brazilian UCS was designed with the same architectural principles as Article 6.4:{" "}
          <strong>public registration, corresponding-adjustment friendly, third-party audited, removal-weighted</strong>.
          Brazil is one of the first countries to submit its 6.4 authorising legislation to the UNFCCC — with
          UCS proposed as one of the first eligible instruments.
        </p>

        <h2>What corporate buyers should do now</h2>
        <ol>
          <li>
            <strong>Audit your existing offset portfolio.</strong> Any Verra REDD+ credit issued before 2024
            will likely need to be re-audited or written down under 6.4.
          </li>
          <li>
            <strong>Prioritise ISIN-anchored instruments.</strong> Instruments already registered on a public
            exchange (like UCS on B3) will transition to 6.4 with minimal disruption.
          </li>
          <li>
            <strong>Build removal weighting into your net-zero plan.</strong> Under SBTi 2030 you cannot claim
            net-zero on avoidance credits alone.
          </li>
          <li>
            <strong>Prepare disclosure infrastructure.</strong> You will need to publish serial numbers and
            registry lookups for every retired credit.
          </li>
        </ol>

        <p>
          GreenBelt's <a href="/registry">public B3 ISIN registry</a> gives you every disclosure field the
          Article 6.4 framework will require, ready to export as a signed PDF for your compliance team.
        </p>
      </>
    ),
  },
  {
    slug: "sbti-2030-disclosure",
    title: "SBTi 2030 — what corporate net-zero teams need to disclose next year",
    excerpt:
      "The Science-Based Targets initiative hits its first hard deadline in 2030 — and disclosure requirements start biting in 2027. Here's the exact evidence file every corporate ESG team should be building right now.",
    date: "2026-02-22",
    read: "7 min read",
    tags: ["SBTi", "Net-zero", "Disclosure", "CSRD"],
    body: (
      <>
        <p>
          By January 2027, every company that has committed to the{" "}
          <strong>Science-Based Targets initiative</strong> (SBTi) — over 7,000 corporates covering 39% of
          global market cap — will be required to publish a <em>disclosure file</em> proving they are on the
          trajectory to their 2030 near-term target. Miss it and your target is publicly de-listed. Get it
          wrong and you're exposed to greenwashing litigation under the EU{" "}
          <strong>Corporate Sustainability Reporting Directive</strong> (CSRD) and California SB 253.
        </p>

        <h2>What "SBTi 2030" actually means</h2>
        <p>
          SBTi requires companies to cut their Scope 1 + 2 emissions by <strong>42% absolute</strong> versus a
          2020 baseline, on the way to net-zero by 2050. Scope 3 (supply chain) gets a separate ambition
          adjusted to each sector. Every claimed reduction must be:
        </p>
        <ul>
          <li><strong>Backed by verifiable data</strong> — invoices, meter reads, or third-party audit trails.</li>
          <li>
            <strong>Distinguished between abatement and offset.</strong> Under the March 2024 SBTi Beyond Value
            Chain Mitigation (BVCM) update, offset credits can no longer count toward the 42% cut. They can
            only address residual emissions post-reduction, and must be{" "}
            <strong>high-quality removal-heavy instruments</strong>.
          </li>
          <li>
            <strong>Publicly disclosed with unique serial numbers.</strong> Every retired credit must be
            traceable to a public registry entry.
          </li>
        </ul>

        <h2>The disclosure file you should be building</h2>
        <p>
          For a mid-market corporate (~$1B revenue), the SBTi 2027 disclosure file typically runs 40–80 pages.
          The parts that are hardest to compile last-minute are:
        </p>
        <ol>
          <li>
            <strong>Retirement register.</strong> A row for every offset credit retired since your baseline
            year, with issuer, ISIN or registry serial, vintage, methodology, third-party audit firm, and
            retirement date.
          </li>
          <li>
            <strong>Removal vs avoidance split.</strong> SBTi weights <em>removals</em> (real forest, DACCS)
            heavier than <em>avoidance</em> (avoided deforestation). Your 2030 residual must be predominantly
            removal-backed.
          </li>
          <li>
            <strong>Corresponding-adjustment status.</strong> If any credits will be internationally
            transferred under Article 6.4 of the Paris Agreement, disclosure of the host country's
            authorisation.
          </li>
          <li>
            <strong>Reversal risk buffer.</strong> Nature-based credits carry non-permanence risk — reforestation
            can burn, land can be re-cleared. SBTi expects buffer-pool disclosure per project.
          </li>
        </ol>

        <h2>Why GreenBelt / UCS is aligned with SBTi 2030</h2>
        <ul>
          <li>
            <strong>Removal-heavy by design.</strong> UCS is priced on <em>protected hectares × timber stock ×
            time</em> — every unit represents standing biomass, not avoided emissions.
          </li>
          <li>
            <strong>Public ISIN registry.</strong> B3 issues each UCS a unique <strong>ISIN code</strong>{" "}
            traceable through your{" "}
            <a href="/registry">/registry</a> page — the exact "unique serial number" SBTi requires.
          </li>
          <li>
            <strong>Tier-1 audit chain.</strong> SGS, TÜV Rheinland, Ernst & Young, ERM — these are the same
            firms already auditing your financial statements, so your compliance team already trusts the audit
            output.
          </li>
          <li>
            <strong>Downloadable signed retirement certificate.</strong> Every retirement generates a signed
            PDF you can drop directly into your SBTi disclosure appendix.
          </li>
          <li>
            <strong>Reversal buffer built-in.</strong> BMV Global withholds 20% of every issuance in a buffer
            pool — disclosed in the methodology page and audited annually.
          </li>
        </ul>

        <h2>The three-year runway</h2>
        <p>
          If your company has an SBTi near-term target and hasn't started building the disclosure file yet,
          you're already behind. The 2027 filing draws on 2020–2026 data — <em>seven years of retirement
          history</em>. Companies waiting until 2026 to switch to registry-backed instruments will show a gap in
          their disclosure that regulators and journalists will notice.
        </p>
        <p>
          The best posture right now: start acquiring a small position in ISIN-anchored, tier-1-audited,
          removal-weighted UCS — retire them as your Q4 2026 vintage — and publish the retirement certificates
          as part of your 2026 annual sustainability report. That's a defensible 2027 disclosure.
        </p>
        <p>
          <a href="/marketplace">Open the marketplace</a> to see the seven live BMV projects, or{" "}
          <a href="/registry">/registry</a> to look up any UCS unit directly on B3.
        </p>
      </>
    ),
  },

];

export function getInsight(slug) {
  return INSIGHTS.find((i) => i.slug === slug);
}
